import React, { useRef, useEffect, useState } from "react";
import classnames from "classnames";
import toast from "react-hot-toast";

import { EventEmitter } from "../../services/EventEmitter/EventEmitter";
import * as Services from "../../services";
import * as Assets from "../../assets";
import * as Utils from "../../utils";
import { Logger } from "../../utils/Logger";

import classes from "./DownloadSelector.module.scss";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("DownloadSelector");

interface IProps {
	awailebleCFR21: boolean;
	awailebleCFR21ClientView?: boolean;
}

export const DownloadSelector = ({ awailebleCFR21, awailebleCFR21ClientView }: IProps): JSX.Element => {
	const [isVisibleBtn, setVisibleBtn] = useState(false);
	const [isVisibleList, setVisibleList] = useState(false);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [isDisabledCFR21, setDisabledCFR21] = useState<boolean>(false);
	const labelRef = useRef<HTMLDivElement>(null);

	const awaitHistoryReport = (reportName: string): void => {
		if (reportName) {
			const startTime = Utils.time.current();
			const interval = setInterval(() => {
				Services.API.WidgetsApi.getReport(reportName)
					.then((url: string) => {
						setTimeout(() => setLoading(false), 300);
						setTimeout(() => {
							window.location.href = url;
						}, 100);
						clearInterval(interval);
					})
					.catch((error: Error) => {
						logger.error(error);
						if (Utils.time.current() - startTime > Utils.time.MILLISECONDS_OF_5MIN) {
							toast.error("Report does not ready for 5 min");
							setLoading(false);
							clearInterval(interval);
						}
					});
			}, 5000);
		}
	};

	useEffect(() => {
		EventEmitter.on("VisibilityAwaitReport", (payload: { reportName: string }) => awaitHistoryReport(payload.reportName));
		EventEmitter.on("VisibilityLockCFR21Report", (payload: { isLocked: boolean }) => setDisabledCFR21(payload.isLocked));
		EventEmitter.on("VisibilityShowBtn", (payload: { isVisible: boolean }) => setVisibleBtn(payload.isVisible));

		return () => {
			EventEmitter.off("VisibilityAwaitReport");
			EventEmitter.off("VisibilityLockCFR21Report");
			EventEmitter.off("VisibilityShowBtn");
		};
	}, []);

	const showHideList = (event: React.MouseEvent<HTMLDivElement>): void => {
		if (!isLoading) {
			event.preventDefault();
			setVisibleList((prev) => !prev);
		}
	};

	const itemOnClickHandler = (event: React.MouseEvent<HTMLDivElement>): void => {
		showHideList(event);
		const type = event.currentTarget.getAttribute("report-type");
		if (isDisabledCFR21 && type === "CFR21") {
			toast.error("CFR21 report does not support a virtual shipment");
			return;
		}
		if (!isLoading) {
			event.preventDefault();
			setLoading(true);
			EventEmitter.emit("VisibilityDownloadReport", { type });
		}
	};

	if (!isVisibleBtn) {
		// eslint-disable-next-line react/jsx-no-useless-fragment
		return (<></>);
	}

	return (
		<div className={classes.downloadSelector}>
			<div
				ref={labelRef}
				className={classnames(
					classes.downloadSelector__label,
					isLoading ? classes.downloadSelector__label_disabled : '',
					isVisibleList ? classes.downloadSelector__label_active : '',
				)}
				onClick={showHideList}>
				{isLoading && (<img className={classes.downloadSelector__loadingImg} alt={''} src={Assets.Loading} />)}
				Download data
			</div>
			{isVisibleList && (
				<div
					className={classes.downloadSelector__list}
					style={{
						width: `${labelRef.current?.getBoundingClientRect().width ?? 0}px`,
					}}>
					<div
						report-type={'pdf'}
						className={classes.downloadSelector__item}
						onClick={itemOnClickHandler}>
						PDF
					</div>
					<div
						report-type={'csv'}
						className={classes.downloadSelector__item}
						onClick={itemOnClickHandler}>
						CSV
					</div>
					{!isDisabledCFR21 && awailebleCFR21 && (
						<div
							report-type={'CFR21'}
							className={classnames(classes.downloadSelector__item, isDisabledCFR21 ? classes.downloadSelector__disabledItem : '')}
							onClick={itemOnClickHandler}>
							CFR21
						</div>
					)}
					{awailebleCFR21ClientView && (
						<div
							report-type={'CFR21_ClientView'}
							className={classnames(classes.downloadSelector__item, isDisabledCFR21 ? classes.downloadSelector__disabledItem : '')}
							onClick={itemOnClickHandler}>
							CFR21 Client
						</div>
					)}
			</div>)}
			{isVisibleList && (
				<div className={classes.downloadSelector__backdrop} onClick={showHideList} />
			)}
		</div>
	);
};
